import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
// components
import { MotionInView, varFadeInUp } from '../../../components/animate';

// ----------------------------------------------------------------------

SectionHeader.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center']),
  titleVariant: PropTypes.string,
  titleComponent: PropTypes.string,
  sx: PropTypes.object,
};

// Shared page-section header so every widget lines up on the same baseline:
// one eyebrow, one title, one optional lede — all fluid across breakpoints.
//
// `titleComponent` controls the rendered tag independently of the visual
// variant: page-level headers pass "h1", in-page sections keep the default
// "h2" so a page reads h1 then h2 without skipping levels.
export default function SectionHeader({
  eyebrow,
  title,
  description,
  align = 'center',
  titleVariant = 'h3',
  titleComponent = 'h2',
  sx,
}) {
  const isCenter = align === 'center';

  return (
    <MotionInView
      variants={varFadeInUp}
      sx={{
        mb: { xs: 3, md: 5 },
        textAlign: isCenter ? 'center' : 'left',
        px: 1,
        ...sx,
      }}
    >
      {eyebrow && (
        <Typography
          variant="subtitle2"
          sx={{
            color: '#DF3E30',
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: 1.2,
          }}
        >
          {eyebrow}
        </Typography>
      )}

      <Typography component={titleComponent} variant={titleVariant} sx={{ color: '#000', mb: description ? 1.5 : 0 }}>
        {title}
      </Typography>

      {description && (
        <Typography
          variant="subtitle1"
          sx={{
            color: (t) => alpha(t.palette.grey[900], 0.64),
            maxWidth: 760,
            mx: isCenter ? 'auto' : 0,
          }}
        >
          {description}
        </Typography>
      )}
    </MotionInView>
  );
}

// ----------------------------------------------------------------------

export function SectionShell({ children, sx }) {
  return <Box sx={{ py: { xs: 5, md: 8 }, ...sx }}>{children}</Box>;
}

SectionShell.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};
